import { ButtonGroup } from "@xola/ui-kit";
import { DateFormat, formatDate, now, toDate } from "common";
import dayjs from "dayjs";
import { omit } from "lodash";
import React, { useMemo } from "react";
import { Report, ReportDateFieldType } from "types";
import { getRelativeDateRange, isInterval } from "../../../models/ReportFilter";
import { ColumnSelector } from "./ColumnSelector";
import { CustomReportActions } from "./CustomReportActions";
import { CustomReportDateRangePicker, DateRange } from "./CustomReportDateRangePicker";
import { ReportFilterOperator, ReportFilterState } from "./ReportFilterState";
import { ReportColumnDef, ReportColumnFilterValueType } from "./SuggestedReports";
import { useCustomReportStore } from "@/stores/customReportFilterStore";

interface CustomReportFilterProps {
    isFetching: boolean;
    report: Report;
    columns: ReportColumnDef[];
    excludedColumns: string[];
    dateFields: string[];
    isFilterChanged: boolean;
    excludeFutureDates?: boolean;
    onRun: () => void;
    onFilterChange: (filter: ReportFilterState) => void;
    onColumnChange: (excludedColumns: string[]) => void;
}

export const CustomReportFilter = ({
    isFetching,
    report,
    columns,
    excludedColumns,
    dateFields,
    onFilterChange,
    isFilterChanged,
    onColumnChange,
    onRun,
    excludeFutureDates,
}: CustomReportFilterProps) => {
    const [filter] = useCustomReportStore((state) => [state.currentFilter]);
    const dateFieldColumns = columns.filter((column) => dateFields.includes(column.field));
    const selectedDateField = getSelectedDateField(filter, dateFields);
    const selectedDateFieldIndex = dateFieldColumns.findIndex((column) => column.field === selectedDateField);
    const dateFieldFilterValue = columns.find((column) => column.field === selectedDateField);
    const dateFieldFilterValueType = dateFieldFilterValue?.filterValueType ?? dateFieldFilterValue?.dateComponent;

    const filterFieldValue = filter[selectedDateField];
    const value = useMemo(
        () => getDateRange(filterFieldValue?.value as any[], dateFieldFilterValueType, filterFieldValue?.rangeName),
        [filterFieldValue, dateFieldFilterValueType],
    );
    const handleDateChange = (dateValue: DateRange) => {
        const updatedFrom = now(dateValue.from);
        let updatedTo = dateValue.to ? now(dateValue.to) : null;

        // Relative ranges like "This Month" span into the future; cap them to today when future dates aren't allowed.
        const endOfToday = now().endOf("day");
        if (excludeFutureDates && updatedTo?.isAfter(endOfToday)) {
            updatedTo = endOfToday;
        }

        if (updatedFrom && updatedTo) {
            const diff = updatedTo.diff(updatedFrom, "year", true);

            if (diff > 1 || updatedTo.isBefore(updatedFrom)) {
                updatedTo = null;
            }
        }

        updateFilter(selectedDateField, {
            from: dateValue.from,
            to: updatedTo ? toDate(updatedTo, false) : null,
        });
    };

    const handleDateFieldChange = (index: number) => {
        const { field } = dateFieldColumns[index];
        updateFilter(field, value!);
    };

    const updateFilter = (dateField: string, value: DateRange) => {
        // Only one date filter can be applied at the time
        const omitDateFields = dateFieldColumns.map((column) => column.field).filter((field) => field !== dateField);

        // This field "items_arrivalDate" is created only for Arrival Date.
        // If we do not remove it when switching to Booking Date, it will always go in the request parameters.
        if (dateField === "createdAt") {
            omitDateFields.push("items_arrivalDate");
        }

        const updatedFilter = omit(filter, omitDateFields) as ReportFilterState;

        onFilterChange({
            ...updatedFilter,
            [dateField]: {
                operator: ReportFilterOperator.RANGE,
                value: [
                    getFilterValue(value.from, dateField, dateFieldFilterValueType)!,
                    getFilterValue(value.to, dateField, dateFieldFilterValueType, true)!,
                ],
                ...(value.rangeName ? { rangeName: value.rangeName } : {}),
            },
        });
    };

    return (
        <div className="flex flex-row items-center justify-between border-b border-gray-light py-2 px-6 print:hidden">
            <div className="flex w-full flex-row items-center justify-start space-x-1 xl:space-x-4">
                {value && (
                    <>
                        <ButtonGroup size="medium" value={selectedDateFieldIndex} onChange={handleDateFieldChange}>
                            {dateFieldColumns.map((column) => (
                                <ButtonGroup.Button key={column.field}>{column.headerName}</ButtonGroup.Button>
                            ))}
                        </ButtonGroup>

                        <CustomReportDateRangePicker
                            disabledDays={isDateDisabled(selectedDateField, value.from)}
                            value={value}
                            isFutureDatesAllowed={!excludeFutureDates}
                            onChange={handleDateChange}
                        />
                    </>
                )}
                <ColumnSelector columns={columns} excludedColumns={excludedColumns} onChange={onColumnChange} />
            </div>

            <CustomReportActions
                isFetching={isFetching}
                isFilterChanged={isFilterChanged}
                selectedDateField={selectedDateField}
                report={report}
                filter={filter}
                excludedColumns={excludedColumns}
                onDateChange={handleDateChange}
                onRun={onRun}
            />
        </div>
    );
};

const isDateDisabled = (dateField: string, fromDate: Date | null) => (date: Date) => {
    if (!fromDate) {
        return false;
    }

    const from = now(fromDate);
    const diff = now(date).diff(from, "year", true);

    /**
     * 1) Disable upcoming dates after 1 year of the `from` date
     * 2) Only the `createdAt` field can't be selected in the future
     */
    return diff > 1 ? true : dateField === ReportDateFieldType.CREATED ? now(date).isAfter(now().endOf("day")) : false;
};

const getSelectedDateField = (filter: ReportFilterState, dateFields: string[]) => {
    const filterFields = Object.keys(filter);
    return filterFields.find((field) => dateFields.includes(field)) ?? dateFields[0];
};

const getDateRange = (
    values?: any[],
    filterValueType?: ReportColumnFilterValueType,
    rangeName?: string,
): DateRange | null => {
    if (!values) {
        return null;
    }

    if (isInterval(values[0])) {
        const [interval, relativeStartDate] = values;
        return getRelativeDateRange(interval, relativeStartDate);
    }

    const [from, to] = values.map((value, index) => {
        const isStartDate = index === 0;
        const date = getDate(value, filterValueType, isStartDate);
        return date;
    });

    const dateRangeValue: DateRange = { from: from!, to };

    if (rangeName) {
        dateRangeValue.rangeName = rangeName;
    }

    return dateRangeValue;
};

const getDate = (value: any, filterValueType?: ReportColumnFilterValueType, isStartDate = true) => {
    if (value && filterValueType === ReportColumnFilterValueType.TIMESTAMP) {
        return toDate(dayjs(value, "X").tz());
    }

    if (value && filterValueType === ReportColumnFilterValueType.DATE) {
        return toDate(now(value), isStartDate);
    }

    return null;
};

const getFilterValue = (
    value: Date | null | undefined,
    dateField: string,
    filterValueType?: ReportColumnFilterValueType,
    rangeEnd = false,
) => {
    if (value && filterValueType === ReportColumnFilterValueType.TIMESTAMP) {
        return dayjs(value).unix();
    }

    if (value && filterValueType === ReportColumnFilterValueType.DATE) {
        const ISOFormatDateFields = [
            ReportDateFieldType.ARRIVAL,
            ReportDateFieldType.START,
            ReportDateFieldType.ITEM_REALIZED,
        ];
        if (ISOFormatDateFields.includes(dateField as ReportDateFieldType)) {
            return formatDate(value, DateFormat.DATE_ISO);
        }

        if (rangeEnd) {
            return formatDate(value, DateFormat.DATE_TIME_ISO_WITHOUT_TIMEZONE);
        }

        return formatDate(value, DateFormat.DATE_TIME_ISO_WITHOUT_TIMEZONE);
    }

    return null;
};
